import { Checkbox, FormControlLabel, Paper, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';

/**
 * POM-6010 — the toggle for items whose lot cannot be trusted.
 *
 * These rows are dropped from the batch by DEFAULT, which is why this arrives ticked. Their
 * reserved lot has expired or gone, so the label would carry a lot that may not match what
 * fulfilment dispenses — and the safe reading of that is not to print it. They stay in the work
 * queue unprinted and every other label prints on its own, so the batch is never held hostage to
 * them either.
 *
 * Unticking is the deliberate opt-out: the rows come back and print with the lot they carry now.
 *
 * The consequence has to be stated on BOTH sides of the toggle, which is why the caption switches:
 * a panel that only described the ticked state would leave the riskier choice unexplained at the
 * moment the user makes it.
 *
 * Its own file because it is almost entirely copy, and copy that has to be exactly right —
 * keeping it out of the dialog leaves the dialog about flow control.
 */
const BulkLotNdcExcludedPanel = ({
  blockedCount,
  printableCount,
  reason,
  excluded,
  canPrintAnyway,
  anyFixableHere,
  onToggle,
}) => {
  const many = blockedCount > 1;
  const them = many ? 'them' : 'it';

  const restClause =
    printableCount > 0
      ? `, and the other ${printableCount} label${printableCount > 1 ? 's' : ''} print${
          printableCount > 1 ? '' : 's'
        } as normal`
      : '';

  return (
    <Paper
      variant='outlined'
      // Amber from the shared constants rather than the warning palette: perfectRxTheme maps
      // palette.warning.main to the brand navy, so 'warning.main' here would paint a navy stripe
      // on a navy-tinted panel and read as information, not as something needing a decision.
      sx={{
        mt: 2,
        p: 2,
        bgcolor: colors.warningAmberLight,
        border: `1px solid ${colors.warningAmberBorder}`,
        borderLeft: `4px solid ${colors.warningAmber}`,
      }}
    >
      {/* POM-6121 — a statement, not a choice, when there is no way back in. The checkbox is
          kept and locked rather than dropped: the count and the reasons read the same either
          way, and an operator who has seen this panel before should not have to relearn it. */}
      <FormControlLabel
        checked={excluded}
        onChange={onToggle}
        disabled={!canPrintAnyway}
        control={<Checkbox />}
        label={
          <Typography variant='body2' sx={{ fontWeight: 600, color: colors.warningAmberText }}>
            {`Leave ${blockedCount} item${many ? 's' : ''} out of this batch — ${reason}`}
          </Typography>
        }
      />
      <Typography variant='caption' color='text.secondary' component='div' sx={{ ml: 4 }}>
        {!canPrintAnyway &&
          (anyFixableHere
            ? `${many ? 'They stay' : 'It stays'} in the work queue unprinted${restClause}. A ` +
              `label needs a lot that can actually be dispensed, so fix ${them} in the grid ` +
              `above to print ${them}.`
            : `${many ? 'They stay' : 'It stays'} in the work queue unprinted${restClause}. A ` +
              `label needs a lot that can actually be dispensed, and nothing here can supply ` +
              `one — an inventory user has to add the product or its stock first.`)}
        {canPrintAnyway &&
          (excluded
            ? `${many ? 'They stay' : 'It stays'} in the work queue unprinted${restClause}. ` +
              `Untick to print ${them} anyway, or fix ${them} in the grid above.`
            : `${many ? 'These labels' : 'This label'} will print with the lot ${
                many ? 'they' : 'it'
              } ${many ? 'carry' : 'carries'} now, which may not match what fulfilment actually ` +
              `dispenses. Fix ${them} in the grid above, or tick this box to leave ${them} out.`)}
      </Typography>
    </Paper>
  );
};

BulkLotNdcExcludedPanel.propTypes = {
  blockedCount: PropTypes.number.isRequired,
  printableCount: PropTypes.number.isRequired,
  reason: PropTypes.string.isRequired,
  excluded: PropTypes.bool.isRequired,
  /** False when the exclusion cannot be undone — the control then reads as a statement. */
  canPrintAnyway: PropTypes.bool,
  /** False when no held-back row has a pencil — "fix them above" would then be a dead end. */
  anyFixableHere: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

BulkLotNdcExcludedPanel.defaultProps = {
  canPrintAnyway: false,
  anyFixableHere: true,
};

export default BulkLotNdcExcludedPanel;
